import { inject } from '@angular/core';
import { GenerateImageUrlWithToken, GetUserId } from '@core/utils';
import { map } from 'rxjs/operators';
import { ChatEntity, ChatMessageEntity } from '@core/entities/chat.entity';
import { plainToInstance } from 'class-transformer';
import { ChatType, SectionType } from '@core/enums';
import { Observable } from 'rxjs';
import { GroupEntity } from '@core/entities';
import { ChatService } from '@core/services';

export class UseChat {
  private readonly _chatService: ChatService = inject(ChatService);

  getConversationsList(): Observable<ChatEntity[]> {
    return this._chatService.getChatList(+GetUserId()).pipe(
      map((response: ChatEntity[]) => {
        return response.map((chat: ChatEntity) => plainToInstance(ChatEntity, chat)).sort((a: ChatEntity, b: ChatEntity) => (a.lastMessage.timestamp > b.lastMessage.timestamp ? -1 : 1));
      }),
    );
  }

  // Room id is a combination of user id and chat id separated by a comma
  getIndividualUserConversationMessages(roomId: string) {
    return this._chatService.getIndividualChat(roomId).pipe(
      map((response: any) => {
        return response.map((chat: ChatMessageEntity) => plainToInstance(ChatMessageEntity, chat));
      }),
    );
  }

  markConversationAsRead(chatId: number, chatType: ChatType) {
    const data = {
      id: chatId,
      type: chatType,
    };
    return this._chatService.readChat(+GetUserId(), data);
  }

  deleteConversation(roomId: string | number) {
    const data = {
      roomId,
    };
    return this._chatService.deleteChat(+GetUserId(), data);
  }

  saveChatMessage(formData: FormData) {
    return this._chatService.createChatMessage(formData);
  }

  deleteChatMessage(messageId: number) {
    const data = {
      msgId: messageId,
    };
    return this._chatService.deleteChatMessage(+GetUserId(), data);
  }

  deleteMultipleChatMessages(ids: number[]) {
    return this._chatService.deleteMultipleChats(+GetUserId(), ids);
  }

  markChatsAsRead() {
    return this._chatService.markAsRead();
  }

  getUserCommonGroups(otherUserId: number): Observable<GroupEntity[]> {
    const data = {
      login_userId: +GetUserId(),
      chat_userId: otherUserId,
      offset: 0,
      limit: 100000,
    };
    return this._chatService.getCommonGroups(data).pipe(
      map((response: any) =>
        response.group.map((group: GroupEntity) => {
          const transformedGroup = plainToInstance(GroupEntity, group);
          transformedGroup.participantsCount = +(group as any).participantCount;
          if (transformedGroup.id) {
            transformedGroup.imageUrl = GenerateImageUrlWithToken(transformedGroup.id, SectionType.GROUP);
          }
          return transformedGroup;
        }),
      ),
    );
  }
}
